import { CarouselDrag } from "@theme/carousel-drag";

/*
 * <carousel-slider>
 * Reusable CSS scroll-snap carousel. Layout, swiping and snapping are plain CSS on the
 * `[data-carousel-track]` element (see `.carousel__track` in carousel.css); this element only
 * wires up the controls:
 *   - `[data-prev]` / `[data-next]` scroll a page back/forward and disable themselves at the ends
 *   - `[data-dots]` is filled with one dot per page, so it works for one or many visible slides
 *     (a page starts at the first slide that didn't fully fit on the previous one)
 *   - in the theme editor, selecting a block scrolls its slide into view
 *   - with a mouse, the track can be dragged (carousel-drag.js)
 *
 * Pages are recalculated on resize. When the track doesn't overflow (e.g. it's restyled as a
 * list on mobile) the element gets `data-static`, so the component can hide its controls.
 *
 * Autoplay: `data-autoplay="5"` advances a page every 5 seconds, reversing direction at either
 * end. It pauses while the carousel is hovered, focused, off-screen, in a background tab or has a
 * block selected in the theme editor, and is skipped entirely for `prefers-reduced-motion`.
 * With `data-autoplay-loop` it jumps back to the first page after the last one instead of reversing.
 *
 * Public API:
 *   element.goToPage(index, { instant })
 *   element.goToSlide(index, { instant })
 *   'carousel:change' event -> detail: { page, pageCount }
 */
if (!customElements.get("carousel-slider")) {
  class CarouselSlider extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        track: "[data-carousel-track]",
        dots: "[data-dots]",
        dot: "[data-dot]",
        prevButton: "[data-prev]",
        nextButton: "[data-next]",
      };
    }

    connectedCallback() {
      this.track = this.querySelector(this.selectors.track);
      if (!this.track) return;

      this.dotsContainer = this.querySelector(this.selectors.dots);
      this.prevButton = this.querySelector(this.selectors.prevButton);
      this.nextButton = this.querySelector(this.selectors.nextButton);
      this.pages = [];
      this.currentPage = -1;
      this.direction = 1;
      this.autoplayDelay = (parseFloat(this.dataset.autoplay) || 0) * 1000;
      this.pauseReasons = new Set();
      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      );
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.addEventListener("click", this.onClick.bind(this), { signal });
      this.track.addEventListener("scroll", this.onScroll.bind(this), {
        passive: true,
        signal,
      });
      // Bubbles up from the selected block, so only blocks inside this carousel are handled
      this.addEventListener(
        "shopify:block:select",
        this.onBlockSelect.bind(this),
        { signal },
      );

      this.drag = new CarouselDrag(this.track, {
        signal,
        getPositions: () => this.slides.map((slide) => this.getSlidePosition(slide)),
        scrollTo: (left) => this.scrollToPosition(left),
        onStart: () => this.pauseAutoplay("drag"),
        onEnd: () => this.resumeAutoplay("drag"),
      });

      this.resizeObserver = new ResizeObserver(() => this.refresh());
      this.resizeObserver.observe(this.track);

      if (this.autoplayDelay) this.setupAutoplay(signal);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.autoplayTimer);
    }

    get slides() {
      return [...this.track.children];
    }

    get maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    /** Scroll position that aligns a slide with the track's scroll-padding. */
    getSlidePosition(slide) {
      const trackRect = this.track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const left =
        slideRect.left -
        trackRect.left -
        this.track.clientLeft +
        this.track.scrollLeft -
        this.scrollPaddingStart;

      return Math.min(Math.max(left, 0), this.maxScroll);
    }

    /** Recalculates the pages, rebuilding the dots when their number changes. */
    refresh() {
      const slides = this.slides;
      const style = getComputedStyle(this.track);
      this.scrollPaddingStart = parseFloat(style.scrollPaddingInlineStart) || 0;
      const scrollPaddingEnd = parseFloat(style.scrollPaddingInlineEnd) || 0;
      const viewport =
        this.track.clientWidth - this.scrollPaddingStart - scrollPaddingEnd;
      const maxScroll = this.maxScroll;
      const pages = [];

      if (maxScroll > 1) {
        const trackLeft =
          this.track.getBoundingClientRect().left +
          this.track.clientLeft -
          this.track.scrollLeft;
        const ends = slides.map(
          (slide) => slide.getBoundingClientRect().right - trackLeft,
        );

        let index = 0;
        while (index < slides.length) {
          const position = this.getSlidePosition(slides[index]);
          pages.push(position);
          if (position >= maxScroll - 1) break;

          // The next page starts at the first slide that doesn't fully fit on this one
          const visibleEnd = position + this.scrollPaddingStart + viewport + 1;
          let next = index + 1;
          while (next < slides.length && ends[next] <= visibleEnd) next++;
          index = next;
        }

        // Trailing track padding: the last page should reach the very end
        pages[pages.length - 1] = Math.max(pages[pages.length - 1], maxScroll);
      }

      const pageCountChanged = pages.length !== this.pages.length;
      this.pages = pages;
      this.toggleAttribute("data-static", pages.length <= 1);
      if (this.drag) this.drag.enabled = pages.length > 1;
      if (pageCountChanged) this.renderDots();

      this.currentPage = -1;
      this.update();
      this.restartAutoplay();
    }

    renderDots() {
      if (!this.dotsContainer) return;

      const label = this.dotsContainer.dataset.label || "[index]";
      const dots =
        this.pages.length > 1
          ? this.pages.map((_, index) => {
              const dot = document.createElement("button");
              dot.type = "button";
              dot.className = "carousel-dots__dot";
              dot.setAttribute("data-dot", "");
              dot.setAttribute("aria-label", label.replace("[index]", index + 1));
              return dot;
            })
          : [];

      this.dotsContainer.replaceChildren(...dots);
      this.dots = dots;
    }

    onScroll() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }

    /** Syncs the dots and arrows with the scroll position. */
    update() {
      const { scrollLeft } = this.track;
      const maxScroll = this.maxScroll;

      if (this.prevButton) this.prevButton.disabled = scrollLeft <= 1;
      if (this.nextButton)
        this.nextButton.disabled = scrollLeft >= maxScroll - 1;

      if (!this.pages.length) return;

      // The closest page wins; the last one is only reached at the very end of the track
      let page = 0;
      this.pages.forEach((position, index) => {
        if (
          Math.abs(position - scrollLeft) <
          Math.abs(this.pages[page] - scrollLeft)
        )
          page = index;
      });
      if (scrollLeft >= maxScroll - 1) page = this.pages.length - 1;

      if (page === this.currentPage) return;
      this.currentPage = page;

      this.dots?.forEach((dot, index) =>
        dot.setAttribute("aria-current", index === page),
      );

      this.dispatchEvent(
        new CustomEvent("carousel:change", {
          bubbles: true,
          detail: { page, pageCount: this.pages.length },
        }),
      );
    }

    scrollToPosition(left, { instant = false } = {}) {
      this.track.scrollTo({
        left,
        behavior: instant || this.reducedMotion.matches ? "instant" : "smooth",
      });
    }

    goToPage(index, options) {
      const position = this.pages[index];
      if (position === undefined) return;

      this.scrollToPosition(position, options);
    }

    goToSlide(index, options) {
      const slide = this.slides[index];
      if (!slide) return;

      this.scrollToPosition(this.getSlidePosition(slide), options);
    }

    onClick(event) {
      const { dot: dotSelector, prevButton, nextButton } = this.selectors;
      if (event.target.closest(`${dotSelector}, ${prevButton}, ${nextButton}`))
        this.restartAutoplay();

      const dot = event.target.closest(this.selectors.dot);
      if (dot && this.dots) return this.goToPage(this.dots.indexOf(dot));
      if (event.target.closest(this.selectors.prevButton))
        return this.goToPage(Math.max(this.currentPage - 1, 0));
      if (event.target.closest(this.selectors.nextButton))
        return this.goToPage(
          Math.min(this.currentPage + 1, this.pages.length - 1),
        );
    }

    /** Theme editor: brings the selected block's slide into view. */
    onBlockSelect(event) {
      const index = this.slides.findIndex((slide) =>
        slide.contains(event.target),
      );
      if (index === -1 || !this.pages.length) return;

      this.goToSlide(index);
    }

    /* Autoplay */

    setupAutoplay(signal) {
      const pauseOn = (target, start, end, reason) => {
        target.addEventListener(start, () => this.pauseAutoplay(reason), { signal });
        target.addEventListener(end, () => this.resumeAutoplay(reason), { signal });
      };

      pauseOn(this, "pointerenter", "pointerleave", "hover");
      pauseOn(this, "shopify:block:select", "shopify:block:deselect", "editor");

      this.addEventListener("focusin", () => this.pauseAutoplay("focus"), { signal });
      this.addEventListener(
        "focusout",
        (event) => {
          if (!this.contains(event.relatedTarget)) this.resumeAutoplay("focus");
        },
        { signal },
      );

      document.addEventListener(
        "visibilitychange",
        () =>
          document.hidden
            ? this.pauseAutoplay("document")
            : this.resumeAutoplay("document"),
        { signal },
      );

      // Starts paused until the carousel is on screen
      this.pauseReasons.add("offscreen");
      this.intersectionObserver = new IntersectionObserver(([entry]) =>
        entry.isIntersecting
          ? this.resumeAutoplay("offscreen")
          : this.pauseAutoplay("offscreen"),
      );
      this.intersectionObserver.observe(this);
    }

    pauseAutoplay(reason) {
      this.pauseReasons.add(reason);
      clearTimeout(this.autoplayTimer);
    }

    resumeAutoplay(reason) {
      this.pauseReasons.delete(reason);
      this.restartAutoplay();
    }

    /** (Re)starts the countdown to the next autoplay step, if autoplay is allowed right now. */
    restartAutoplay() {
      clearTimeout(this.autoplayTimer);
      if (
        !this.autoplayDelay ||
        this.pauseReasons.size ||
        this.pages.length <= 1 ||
        this.reducedMotion.matches
      )
        return;

      this.autoplayTimer = setTimeout(() => {
        this.autoplayStep();
        this.restartAutoplay();
      }, this.autoplayDelay);
    }

    /** Moves one page in the current direction, turning around at either end (or looping). */
    autoplayStep() {
      const lastPage = this.pages.length - 1;
      if (this.hasAttribute("data-autoplay-loop")) {
        this.goToPage(this.currentPage >= lastPage ? 0 : this.currentPage + 1);
        return;
      }

      if (this.currentPage >= lastPage) this.direction = -1;
      else if (this.currentPage <= 0) this.direction = 1;

      this.goToPage(this.currentPage + this.direction);
    }
  }

  customElements.define("carousel-slider", CarouselSlider);
}
